import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TenantProvider } from './contexts/TenantContext';
import { TourProvider } from './contexts/TourContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { DEFAULT_TOAST_DURATION } from './constants';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import LoadingSpinner from './components/LoadingSpinner';

// =============================================================================
// LAZY-LOADED COMPONENTS (Code Splitting)
// =============================================================================
// Cada componente se carga solo cuando se necesita, reduciendo el bundle inicial

// Auth Components
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

// App Components
// const Dashboard = lazy(() => import('./components/Dashboard')); // REMOVED: Route /dashboard eliminated in v3.3.6
const PaymentsList = lazy(() => import('./components/PaymentsList'));
const NotificationHistory = lazy(() => import('./components/NotificationHistory'));
const Channels = lazy(() => import('./components/Channels'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const Profile = lazy(() => import('./components/Profile'));
const HelpPage = lazy(() => import('./components/help/HelpPage'));
const Templates = lazy(() => import('./components/Templates'));
const TemplateTypes = lazy(() => import('./components/TemplateTypes'));
const TemplateLibraryPage = lazy(() => import('./components/TemplateLibraryPage'));
const ApiDocumentation = lazy(() => import('./components/ApiDocumentation'));
const UnifiedApiDocs = lazy(() => import('./components/UnifiedApiDocs')); // v3.3.6 - Unified API Docs
const SystemDocumentation = lazy(() => import('./components/SystemDocumentation'));
const PublisherSystems = lazy(() => import('./components/PublisherSystems'));
const Billers = lazy(() => import('./components/Billers'));
const BillerDashboard = lazy(() => import('./components/BillerDashboard'));
const NotificationVectors = lazy(() => import('./components/NotificationVectors'));
const PurgeManagement = lazy(() => import('./components/PurgeManagement'));
const Prices = lazy(() => import('./components/Prices'));
const Medios = lazy(() => import('./components/Medios')); // v3.12.0 - Medios (Canales + Precios)
const Logs = lazy(() => import('./components/Logs'));
const TenantManagement = lazy(() => import('./components/admin/TenantManagement'));
const ConnectionMonitor = lazy(() => import('./components/ConnectionMonitor'));
const Schedulers = lazy(() => import('./components/Schedulers'));
const AdminAIConfiguration = lazy(() => import('./components/admin/AdminAIConfiguration'));
const HistoricPayments = lazy(() => import('./components/HistoricPayments'));
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const CustomersManagement = lazy(() => import('./components/CustomersManagement'));
const AdvancedScoreDashboard = lazy(() => import('./components/AdvancedScoreDashboard'));
const AlertsPanel = lazy(() => import('./components/AlertsPanel'));
const AdvancedConfigPanel = lazy(() => import('./components/AdvancedConfigPanel'));
const IndustryManagement = lazy(() => import('./components/IndustryManagement'));
const NegotiationPlansManager = lazy(() => import('./components/NegotiationPlansManager'));
const NegotiationCampaignDashboard = lazy(() => import('./components/NegotiationCampaignDashboard'));
const AcceptNegotiationPlan = lazy(() => import('./components/AcceptNegotiationPlan'));
const RoleManagement = lazy(() => import('./components/RoleManagement'));
const AuditLogsViewer = lazy(() => import('./components/AuditLogsViewer'));
const AuditStatsDashboard = lazy(() => import('./components/AuditStatsDashboard'));
const SecurityEventsPanel = lazy(() => import('./components/SecurityEventsPanel'));
const DefaultRedirect = lazy(() => import('./components/DefaultRedirect'));
const PublisherThemeManager = lazy(() => import('./components/PublisherThemeManager'));
const MonitoringDashboard = lazy(() => import('./components/MonitoringDashboard'));
const PublisherUserManagement = lazy(() => import('./components/PublisherUserManagement')); // v3.1.0
const BillerUserManagement = lazy(() => import('./components/BillerUserManagement')); // v3.1.0
const PublisherDashboard = lazy(() => import('./components/PublisherDashboard')); // v3.1.0
const SeriesManager = lazy(() => import('./components/SeriesManager')); // v3.2.0 - Assets Module
const OnboardingManager = lazy(() => import('./components/OnboardingManager')); // v3.5.0 - Onboarding Module
const LogosManager = lazy(() => import('./components/LogosManager')); // v3.5.1 - Platform Logos
const HelpTooltipsManager = lazy(() => import('./components/HelpTooltipsManager')); // v3.6.0 - Help Tooltips System
const PlatformDashboard = lazy(() => import('./components/PlatformDashboard')); // v3.2.0 - Platform Dashboard
const PlatformConverters = lazy(() => import('./components/PlatformConverters')); // File Converters catalog
const IAExchange = lazy(() => import('./components/IAExchange')); // v3.3.7 - IA-Exchange Documents
const OAuthAdminManager = lazy(() => import('./components/OAuthAdminManager')); // v3.4.2 - OAuth Admin
const PasswordPolicies = lazy(() => import('./components/PasswordPolicies')); // Password Policies (IAM)
const UserMessages = lazy(() => import('./components/UserMessages')); // v3.5.0 - User Messages/Notifications
const BackgroundImportAdmin = lazy(() => import('./components/admin/BackgroundImportAdmin')); // v3.5.1 - Background Import Admin
const SchedulerGroupsManager = lazy(() => import('./components/admin/SchedulerGroupsManager')); // v3.5.0 - Scheduler Groups
const PackagesPage = lazy(() => import('./components/packages/PackagesPage')); // v3.10.0 - Packages and Licenses
const CountryManagement = lazy(() => import('./components/CountryManagement')); // v3.16.0 - Countries Management
const BanksManagement = lazy(() => import('./components/BanksManagement')); // Conciliación - Plataforma → Bancos
const GlobalChannelsManagement = lazy(() => import('./components/GlobalChannelsManagement')); // Conciliación - Plataforma → Canales Globales
const PlatformCardBrands = lazy(() => import('./components/PlatformCardBrands')); // Plataforma → Marcas de Tarjeta
const TranslationManagement = lazy(() => import('./components/TranslationManagement')); // v3.17.0 - Multi-language
const PlatformTemplates = lazy(() => import('./components/PlatformTemplates')); // v3.18.0 - Platform Templates
const OperationsPage = lazy(() => import('./components/admin/OperationsPage')); // v3.19.0 - Operations (Force Logout)
const PartnerPrograms = lazy(() => import('./components/admin/PartnerPrograms')); // Partner Programs
const TcDocumentsPage = lazy(() => import('./components/tc/TcDocumentsPage')); // v3.22.0 - T&C
const AnnouncementsPage = lazy(() => import('./components/announcements/AnnouncementsPage')); // v3.23.0 - Announcements

// =============================================================================
// WRAPPER COMPONENT FOR SUSPENSE
// =============================================================================
// Envuelve cada ruta con Suspense para manejar el loading de componentes lazy
const SuspenseRoute = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

function App() {
  return (
    <AuthProvider>
      <LocaleProvider>
      <PermissionsProvider>
        <TourProvider>
          <TenantProvider>
            <ConfirmProvider>
              <Router>
                <ThemeProvider>
                <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <SuspenseRoute>
                    <Login />
                  </SuspenseRoute>
                } />
                <Route path="/register" element={
                  <SuspenseRoute>
                    <Register />
                  </SuspenseRoute>
                } />
                <Route path="/verify-email" element={
                  <SuspenseRoute>
                    <VerifyEmail />
                  </SuspenseRoute>
                } />
                <Route path="/forgot-password" element={
                  <SuspenseRoute>
                    <ForgotPassword />
                  </SuspenseRoute>
                } />
                <Route path="/reset-password" element={
                  <SuspenseRoute>
                    <ResetPassword />
                  </SuspenseRoute>
                } />
                <Route path="/pagar/:paymentReference" element={
                  <SuspenseRoute>
                    <PaymentPage />
                  </SuspenseRoute>
                } />
                <Route path="/aceptar-plan/:acceptanceToken" element={
                  <SuspenseRoute>
                    <AcceptNegotiationPlan />
                  </SuspenseRoute>
                } />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={
                    <SuspenseRoute>
                      <DefaultRedirect />
                    </SuspenseRoute>
                  } />
                  <Route path="payments" element={
                    <SuspenseRoute>
                      <PaymentsList />
                    </SuspenseRoute>
                  } />
                  <Route path="historic" element={
                    <SuspenseRoute>
                      <HistoricPayments />
                    </SuspenseRoute>
                  } />
                  <Route path="notifications" element={
                    <SuspenseRoute>
                      <NotificationHistory />
                    </SuspenseRoute>
                  } />
                  <Route path="notification-vectors" element={
                    <SuspenseRoute>
                      <NotificationVectors />
                    </SuspenseRoute>
                  } />
                  <Route path="ia-exchange" element={
                    <SuspenseRoute>
                      <IAExchange />
                    </SuspenseRoute>
                  } />
                  <Route path="templates" element={
                    <SuspenseRoute>
                      <Templates />
                    </SuspenseRoute>
                  } />
                  <Route path="template-types" element={
                    <SuspenseRoute>
                      <TemplateTypes />
                    </SuspenseRoute>
                  } />
                  <Route path="template-library" element={
                    <SuspenseRoute>
                      <TemplateLibraryPage />
                    </SuspenseRoute>
                  } />
                  {/* Assets Module (v3.2.0) - Templates & Series - No tabs, sidebar navigation only */}
                  <Route path="assets/platform-templates" element={
                    <SuspenseRoute>
                      <PlatformTemplates />
                    </SuspenseRoute>
                  } />
                  <Route path="assets/templates" element={
                    <SuspenseRoute>
                      <TemplateLibraryPage />
                    </SuspenseRoute>
                  } />
                  <Route path="assets/series" element={
                    <SuspenseRoute>
                      <SeriesManager />
                    </SuspenseRoute>
                  } />
                  <Route path="assets/onboarding" element={
                    <SuspenseRoute>
                      <OnboardingManager />
                    </SuspenseRoute>
                  } />
                  <Route path="assets/logos" element={
                    <SuspenseRoute>
                      <LogosManager />
                    </SuspenseRoute>
                  } />
                  <Route path="assets/help-tooltips" element={
                    <SuspenseRoute>
                      <HelpTooltipsManager />
                    </SuspenseRoute>
                  } />
                  <Route path="docs" element={
                    <SuspenseRoute>
                      <SystemDocumentation />
                    </SuspenseRoute>
                  } />
                  <Route path="apidoc" element={
                    <SuspenseRoute>
                      <UnifiedApiDocs />
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-systems" element={
                    <SuspenseRoute>
                      <PublisherSystems />
                    </SuspenseRoute>
                  } />
                  <Route path="billers" element={
                    <SuspenseRoute>
                      <Billers />
                    </SuspenseRoute>
                  } />
                  <Route path="billers/:biller_id/dashboard" element={
                    <SuspenseRoute>
                      <BillerDashboard />
                    </SuspenseRoute>
                  } />
                  <Route path="customers/:publisher_system_id/:biller_id" element={
                    <SuspenseRoute>
                      <CustomersManagement />
                    </SuspenseRoute>
                  } />
                  <Route path="channels" element={
                    <SuspenseRoute>
                      <Channels />
                    </SuspenseRoute>
                  } />
                  <Route path="profile" element={
                    <SuspenseRoute>
                      <Profile />
                    </SuspenseRoute>
                  } />
                  <Route path="help" element={
                    <SuspenseRoute>
                      <HelpPage />
                    </SuspenseRoute>
                  } />
                  <Route path="messages" element={
                    <SuspenseRoute>
                      <UserMessages />
                    </SuspenseRoute>
                  } />

                  {/* Collection Score Routes */}
                  <Route path="collection-scores/advanced" element={
                    <SuspenseRoute>
                      <AdvancedScoreDashboard />
                    </SuspenseRoute>
                  } />
                  <Route path="collection-scores/alerts" element={
                    <SuspenseRoute>
                      <AlertsPanel />
                    </SuspenseRoute>
                  } />
                  <Route path="collection-scores/config" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'score', action: 'view', subModule: 'configuracion' }}>
                        <AdvancedConfigPanel />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* Negotiation Routes */}
                  <Route path="negotiation/plans" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'renegociacion', action: 'view', subModule: 'planes' }}>
                        <NegotiationPlansManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="negotiation/campaigns" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'renegociacion', action: 'view', subModule: 'campanas' }}>
                        <NegotiationCampaignDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* IAM Routes */}
                  <Route path="users" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'usuarios', action: 'view' }}>
                        <UserManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* v3.1.0: Publisher User Management - For Publisher Admin */}
                  <Route path="publisher/users" element={
                    <SuspenseRoute>
                      <PublisherUserManagement />
                    </SuspenseRoute>
                  } />

                  {/* v3.1.0: Biller User Management - For Biller Admin */}
                  <Route path="biller/users" element={
                    <SuspenseRoute>
                      <BillerUserManagement />
                    </SuspenseRoute>
                  } />

                  <Route path="roles" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'roles', action: 'view' }}>
                        <RoleManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* Password Policies (IAM) */}
                  <Route path="policies" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'policies', action: 'view' }}>
                        <PasswordPolicies />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* v3.4.2: OAuth Administration */}
                  <Route path="oauth-admin" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'oauth_admin', action: 'view' }}>
                        <OAuthAdminManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* v3.10.0: Packages and Licenses Management */}
                  <Route path="packages" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <PackagesPage />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/logs" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'auditoria', action: 'view' }}>
                        <AuditLogsViewer />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/stats" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'auditoria', action: 'view' }}>
                        <AuditStatsDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/security" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'auditoria', action: 'view' }}>
                        <SecurityEventsPanel />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="purge" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <PurgeManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="platform/dashboard" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'kpi', action: 'view' }}>
                        <PlatformDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="platform/converters" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'file_converters', action: 'view' }}>
                        <PlatformConverters />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="prices" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <Prices />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="medios" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <Medios />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="logs" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <Logs />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* Admin Module Routes */}
                  <Route path="admin/tenants" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'tenants', action: 'view' }}>
                        <TenantManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin/connections" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'conexiones', action: 'view' }}>
                        <ConnectionMonitor />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin/schedulers" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'schedulers', action: 'view' }}>
                        <Schedulers />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin/ai-config" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'ai_config', action: 'view' }}>
                        <AdminAIConfiguration />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin/import" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <BackgroundImportAdmin />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin/scheduler-groups" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'schedulers', action: 'view' }}>
                        <SchedulerGroupsManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* v3.19.0: Operations (Force Logout) */}
                  <Route path="admin/operations" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'operations', action: 'view' }}>
                        <OperationsPage />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  <Route path="partners" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'publishers', action: 'view' }}>
                        <PartnerPrograms />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  <Route path="tc" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'terms_conditions', action: 'view' }}>
                        <TcDocumentsPage />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="announcements" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'novedades', action: 'view' }}>
                        <AnnouncementsPage />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  <Route path="industries" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'segmentos', action: 'view' }}>
                        <IndustryManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* v3.16.0: Countries Management */}
                  <Route path="countries" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'paises', action: 'view' }}>
                        <CountryManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* Conciliación: Plataforma → Bancos */}
                  <Route path="banks" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'banks_catalog', action: 'view' }}>
                        <BanksManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* Conciliación: Plataforma → Canales Globales */}
                  <Route path="global-channels" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'channels_catalog', action: 'view' }}>
                        <GlobalChannelsManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* Plataforma → Marcas de Tarjeta */}
                  <Route path="card-brands" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'channels_catalog', action: 'view' }}>
                        <PlatformCardBrands />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  {/* v3.17.0: Translation Management */}
                  <Route path="translations" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'traducciones', action: 'view' }}>
                        <TranslationManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-dashboard/:publisherId" element={
                    <SuspenseRoute>
                      <ProtectedRoute>
                        <PublisherDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-themes/:publisherId" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'publishers', action: 'view' }}>
                        <PublisherThemeManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="monitoring" element={
                    <SuspenseRoute>
                      <ProtectedRoute requirePermission={{ module: 'monitoreo', action: 'view' }}>
                        <MonitoringDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                </Route>

                {/* Fallback route - redirect to DefaultRedirect which handles role-based routing */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: DEFAULT_TOAST_DURATION,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    style: {
                      background: '#10b981',
                    },
                  },
                  error: {
                    duration: 5000,
                    style: {
                      background: '#ef4444',
                    },
                  },
                }}
              />
                </div>
              </ThemeProvider>
              </Router>
            </ConfirmProvider>
          </TenantProvider>
        </TourProvider>
      </PermissionsProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}

export default App;
